import React, { useState, useRef, useEffect, useCallback } from "react";
import { getStatusBucket } from "../utils/caseStatus";
import {
  doc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  limit,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import {
  MoreVertical,
  Users,
  User,
  UserCheck,
  Mail,
  Phone,
  Loader2,
} from "lucide-react";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import featureGatingService from "../services/featureGatingService";
import SharingLimitDialog from "../components/SharingLimitDialog";
import { copyLabelsToUser } from "../services/labelService";
import { addTimelineEvent } from "../services/timelineService";

// ToggleButton Component
const ToggleButton = ({ isChecked, onToggle, disabled = false }) => {
  return (
    <label
      className={`inline-flex items-center ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        }`}
    >
      <input
        type="checkbox"
        checked={isChecked}
        onChange={onToggle}
        className="sr-only peer"
        disabled={disabled}
      />
      <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
    </label>
  );
};

function AllowAccess({ caseData, caseId, onAccessChange, caseCollection: caseCollectionProp }) {
  const { globalUser } = useAuth();

  // State Variables
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOption, setSelectedOption] = useState({
    type: "search",
    value: "Name",
  });
  const [userTeams, setUserTeams] = useState([]);
  const [currentCaseTeam, setCurrentCaseTeam] = useState([]);
  const [memberToggleStates, setMemberToggleStates] = useState({});
  const [teamAccessList, setTeamAccessList] = useState([]);
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [isGlobalToggleChecked, setIsGlobalToggleChecked] = useState(false);
  const [showTeamMembers, setShowTeamMembers] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Loading and Error States
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Subscription Limit States
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [limitDialogData, setLimitDialogData] = useState({
    currentCount: 0,
    maxLimit: 0,
    planName: '',
    upgradeMessage: ''
  });
  const [teamLimitInfo, setTeamLimitInfo] = useState(null);

  // Refs
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Fetch team member limit info
  const fetchTeamLimitInfo = useCallback(async () => {
    if (!globalUser?.uid) return;
    try {
      const info = await featureGatingService.canAddTeamMember(globalUser.uid);
      setTeamLimitInfo(info);
    } catch (error) {
      console.error('Error fetching team limit info:', error);
    }
  }, [globalUser?.uid]);

  // Fetch limit info on mount
  useEffect(() => {
    fetchTeamLimitInfo();
  }, [fetchTeamLimitInfo]);

  // Helper function to get case document reference
  const getCaseDocRef = async () => {
    if (!caseId) return { ref: null, exists: false, data: null };
    const collection = getStatusBucket(caseData?.status);
    const caseRef = doc(db, collection, caseId);

    try {
      const caseSnap = await getDoc(caseRef);
      if (caseSnap.exists()) {
        return { ref: caseRef, exists: true, data: caseSnap.data() };
      }
    } catch (err) {
      console.error("Error checking case document:", err);
      setError("Failed to load case details.");
    }
    return { ref: null, exists: false, data: null };
  };

  // Initialize current case team from caseData
  useEffect(() => {
    if (caseData?.team) {
      setCurrentCaseTeam(Array.isArray(caseData.team) ? caseData.team : []);
    }
  }, [caseData]);

  // Fetch team access information
  useEffect(() => {
    const fetchTeamAccess = async () => {
      if (!caseData?.team || caseData.team.length === 0) {
        setTeamAccessList([]);
        return;
      }

      try {
        // Get all teams where current case members are present
        const teamsQuery = query(
          collection(db, "teams"),
          where("ownerId", "==", globalUser?.uid)
        );
        const querySnapshot = await getDocs(teamsQuery);

        const accessibleTeams = [];

        for (const teamDoc of querySnapshot.docs) {
          const teamData = teamDoc.data();
          const teamMembers = teamData.members || [];

          // Skip default team — individual shares show in "Individual Access" only
          if (teamData.isDefault === true) continue;

          // Check if all team members have access to the case
          if (teamMembers.length > 0) {
            const allMembersHaveAccess = teamMembers.every(memberId =>
              caseData.team.includes(memberId)
            );

            if (allMembersHaveAccess) {
              accessibleTeams.push({
                id: teamDoc.id,
                name: teamData.teamName,
                members: teamMembers,
                memberCount: teamMembers.length
              });
            }
          }
        }

        setTeamAccessList(accessibleTeams);
      } catch (err) {
        console.error("Error fetching team access:", err);
      }
    };

    fetchTeamAccess();
  }, [caseData, globalUser]);

  // Debounce Input Value for Search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [inputValue]);

  // Trigger Search when Debounced Input Changes
  useEffect(() => {
    if (selectedOption.type === "search" && debouncedInputValue.trim()) {
      executeSearch(debouncedInputValue.trim());
    } else if (
      selectedOption.type === "search" &&
      !debouncedInputValue.trim()
    ) {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [debouncedInputValue, selectedOption]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch user's teams
  useEffect(() => {
    if (!globalUser?.uid) return;
    const fetchTeams = async () => {
      setLoadingTeams(true);
      try {
        const teamsQuery = query(
          collection(db, "teams"),
          where("ownerId", "==", globalUser.uid)
        );
        const querySnapshot = await getDocs(teamsQuery);
        const fetchedTeams = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUserTeams(fetchedTeams);
      } catch (err) {
        console.error("Error fetching teams:", err);
        setError("Failed to load your teams.");
      } finally {
        setLoadingTeams(false);
      }
    };
    fetchTeams();
  }, [globalUser]);

  // Initialize toggle states for search results
  useEffect(() => {
    if (showSearchResults && searchResults.length > 0) {
      const initialStates = {};
      searchResults.forEach((result) => {
        if (result?.uid) {
          initialStates[result.uid] = currentCaseTeam.includes(result.uid);
        }
      });
      setMemberToggleStates(initialStates);
    }
  }, [searchResults, currentCaseTeam, showSearchResults]);

  // Calculate global toggle state for team members
  useEffect(() => {
    if (!showTeamMembers || !showSearchResults || searchResults.length === 0) {
      setIsGlobalToggleChecked(false);
      return;
    }

    // Check if we're viewing team members (not individual search)
    if (selectedOption.type === "team") {
      const allChecked = searchResults.every(
        (member) => member?.uid && memberToggleStates[member.uid]
      );
      setIsGlobalToggleChecked(allChecked);
    }
  }, [memberToggleStates, searchResults, showSearchResults, showTeamMembers, selectedOption]);

  // Search Function
  const executeSearch = async (searchTerm) => {
    if (selectedOption.type !== "search" || !searchTerm) return;

    const searchType = selectedOption.value;
    const isClientSearch = searchType === "Clients";
    const collectionRef = collection(db, isClientSearch ? "client" : "lawyers");
    let q;

    setLoadingSearch(true);
    setError(null);
    setSearchResults([]);
    setShowSearchResults(false);

    try {
      if (searchType === "Clients") {
        q = query(
          collectionRef,
          where("name", ">=", searchTerm),
          where("name", "<=", searchTerm + "\uf8ff"),
          limit(10)
        );
      } else if (searchType === "Name") {
        q = query(
          collectionRef,
          where("name", ">=", searchTerm),
          where("name", "<=", searchTerm + "\uf8ff"),
          limit(10)
        );
      } else if (searchType === "Mobile Number") {
        q = query(
          collectionRef,
          where("mobileNumber", ">=", searchTerm),
          where("mobileNumber", "<=", searchTerm + "\uf8ff"),
          limit(10)
        );
      } else if (searchType === "Email") {
        q = query(
          collectionRef,
          where("email", ">=", searchTerm.toLowerCase()),
          where("email", "<=", searchTerm.toLowerCase() + "\uf8ff"),
          limit(10)
        );
      } else {
        throw new Error("Invalid search type");
      }

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const results = querySnapshot.docs.map((docSnap) => ({
          uid: docSnap.id,
          ...docSnap.data(),
          _isClient: isClientSearch,
        }));
        setSearchResults(results);
        setShowSearchResults(true);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    } catch (err) {
      console.error("Error during search:", err);
      setError("Search failed. Please try again.");
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setLoadingSearch(false);
    }
  };

  // Fetch team members when a team is selected
  const fetchTeamMembers = async (teamId) => {
    if (!teamId) return;
    setLoadingMembers(true);
    setLoadingSearch(true);
    setShowSearchResults(false);
    setShowTeamMembers(true);
    setSearchResults([]);
    setError(null);

    try {
      const teamDocRef = doc(db, "teams", teamId);
      const teamDocSnap = await getDoc(teamDocRef);

      if (teamDocSnap.exists()) {
        const memberIds = teamDocSnap.data().members || [];
        if (memberIds.length > 0) {
          const memberPromises = memberIds.map((id) =>
            getDoc(doc(db, "lawyers", id))
          );
          const memberDocs = await Promise.all(memberPromises);
          const membersData = memberDocs
            .filter((docSnap) => docSnap.exists())
            .map((docSnap) => ({ uid: docSnap.id, ...docSnap.data() }));
          setSearchResults(membersData);
          setShowSearchResults(true);
        } else {
          // Empty team
          setSearchResults([]);
          setShowSearchResults(true);
        }
      } else {
        setError("Selected team not found.");
      }
    } catch (err) {
      console.error("Error fetching team members:", err);
      setError("Failed to load team members.");
    } finally {
      setLoadingMembers(false);
      setLoadingSearch(false);
    }
  };

  // Handle option selection from dropdown
  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setInputValue("");
    setDebouncedInputValue("");
    setSearchResults([]);
    setShowSearchResults(false);
    setShowTeamMembers(false);
    setShowDropdown(false);
    setError(null);

    if (option.type === "team") {
      fetchTeamMembers(option.value);
    }
  };

  // Check subscription limit before adding users
  const checkSharingLimit = async (usersToAdd) => {
    if (!globalUser?.uid) return false;

    try {
      const [result, existingMemberIds] = await Promise.all([
        featureGatingService.canAddTeamMember(globalUser.uid),
        featureGatingService.getUniqueTeamMemberIds(globalUser.uid),
      ]);
      const { maxLimit, planName, upgradeMessage } = result;
      const currentCount = existingMemberIds.size;

      // Only count members that are truly new (not already in any team)
      const newMemberCount = usersToAdd.filter(uid => !existingMemberIds.has(uid)).length;

      if (currentCount + newMemberCount > maxLimit) {
        setLimitDialogData({
          currentCount,
          maxLimit,
          planName,
          upgradeMessage,
        });
        setShowLimitDialog(true);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking sharing limit:', error);
      return false; // Block on error to enforce limits
    }
  };

  // Update case team in Firestore
  const updateCaseTeam = async (updateAction, userIds) => {
    if (!caseId || !userIds || userIds.length === 0) return;

    // Check limit before adding users
    if (updateAction === "add") {
      const canProceed = await checkSharingLimit(userIds);
      if (!canProceed) {
        return; // Limit exceeded, dialog shown
      }
    }

    setIsUpdating(true);
    const { ref: caseDocRef, exists, data: caseDataFresh } = await getCaseDocRef();
    if (!exists || !caseDocRef) {
      setError("Case document not found to update.");
      setIsUpdating(false);
      return;
    }

    try {
      const operation = updateAction === "add" ? arrayUnion : arrayRemove;
      await updateDoc(caseDocRef, {
        team: operation(...userIds),
      });

      // Sync with default team for individual shares
      if (selectedOption.type === "search" && updateAction === "add") {
        for (const userId of userIds) {
          await featureGatingService.addMemberToDefaultTeam(globalUser.uid, userId);
        }
      } else if (updateAction === "remove") {
        for (const userId of userIds) {
          await featureGatingService.removeMemberFromDefaultTeam(globalUser.uid, userId);
        }
      }

      // Copy labels to new users when adding
      if (updateAction === "add" && caseDataFresh?.labels && caseDataFresh.labels.length > 0) {
        const ownerId = caseDataFresh.owner || globalUser?.uid;
        const labelsByUser = caseDataFresh.labelsByUser || {};

        // Ensure owner's labels are stored
        if (!labelsByUser[ownerId]) {
          labelsByUser[ownerId] = caseDataFresh.labels;
        }

        for (const userId of userIds) {
          try {
            // Copy labels and get the mapping of owner's labelIds to recipient's labelIds
            const labelMapping = await copyLabelsToUser(ownerId, userId, caseDataFresh.labels);

            // Convert owner's label IDs to recipient's label IDs
            const recipientLabelIds = caseDataFresh.labels
              .map(labelId => labelMapping[labelId])
              .filter(Boolean);

            // Store recipient's label IDs in the case
            labelsByUser[userId] = recipientLabelIds;
          } catch (error) {
            console.error(`Error copying labels to user ${userId}:`, error);
            // Continue with other users even if one fails
          }
        }

        // Update the case with labelsByUser mapping
        await updateDoc(caseDocRef, {
          labelsByUser: labelsByUser
        });
      }

      // Log timeline events for sharing/unsharing
      const timelineCollection = caseCollectionProp || (getStatusBucket(caseData?.status));
      const isClientSharing = searchResults.some((r) => r._isClient);
      for (const userId of userIds) {
        const matchedUser = searchResults.find((r) => r.uid === userId);
        const userName = matchedUser?.name || "Unknown";
        const userEmail = matchedUser?.email || "";
        const userType = isClientSharing ? "Client" : "Lawyer";
        const eventType =
          updateAction === "add"
            ? isClientSharing ? "client_shared" : "case_shared"
            : isClientSharing ? "client_unshared" : "case_unshared";
        const eventTitle =
          updateAction === "add"
            ? `Access granted to ${userName}`
            : `Access revoked from ${userName}`;

        const changes = {};
        changes["User"] = { old: "—", new: userName };
        if (userEmail) changes["Email"] = { old: "—", new: userEmail };
        changes["Type"] = { old: "—", new: userType };
        changes["Access"] = {
          old: updateAction === "add" ? "No access" : "Has access",
          new: updateAction === "add" ? "Granted" : "Revoked",
        };

        addTimelineEvent(caseId, timelineCollection, {
          type: eventType,
          title: eventTitle,
          description: isClientSharing
            ? `${updateAction === "add" ? "Shared with" : "Unshared from"} client: ${userName}`
            : `${updateAction === "add" ? "Shared with" : "Unshared from"} lawyer: ${userName}`,
          performedBy: globalUser?.uid || "",
          performedByName: globalUser?.name || globalUser?.displayName || "",
          changes: changes,
        });
      }

      await onAccessChange(); // Refresh parent component data
      await fetchTeamLimitInfo(); // Refresh team member count
    } catch (error) {
      console.error(
        `Error ${updateAction === "add" ? "adding" : "removing"} user(s):`,
        error
      );
      setError(
        `Failed to update access for ${userIds.length > 1 ? "members" : "member"
        }.`
      );
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle individual toggle from search results
  const handleSearchResultToggle = async (memberUid) => {
    if (!memberUid) return;
    const isCurrentlyChecked = memberToggleStates[memberUid];
    const action = isCurrentlyChecked ? "remove" : "add";

    // For adds, check limit BEFORE flipping toggle
    if (action === "add") {
      const canProceed = await checkSharingLimit([memberUid]);
      if (!canProceed) return;
    }

    setMemberToggleStates((prev) => ({
      ...prev,
      [memberUid]: !isCurrentlyChecked,
    }));
    updateCaseTeam(action, [memberUid]);
  };

  // Handle removing individual access
  const handleRemoveIndividualAccess = async (uid) => {
    await updateCaseTeam("remove", [uid]);
  };

  // Handle removing team access
  const handleRemoveTeamAccess = async (teamMembers) => {
    await updateCaseTeam("remove", teamMembers);
  };

  // Global toggle handler for team members
  const handleGlobalToggle = () => {
    const isCurrentlyGloballyChecked = isGlobalToggleChecked;
    const action = isCurrentlyGloballyChecked ? "remove" : "add";
    const memberUids = searchResults
      .map((member) => member.uid)
      .filter((uid) => !!uid);

    if (memberUids.length === 0) return;

    const newStates = {};
    memberUids.forEach((uid) => {
      newStates[uid] = !isCurrentlyGloballyChecked;
    });
    setMemberToggleStates(newStates);
    updateCaseTeam(action, memberUids);
  };

  // Render contact details with privacy settings
  const renderContactDetails = (contact) => {
    if (!contact) return null;
    const settings = contact.settings || {};
    const isClient = contact._isClient === true;
    const details = {
      name: contact.name,
      mobileNumber:
        isClient
          ? contact.mobileNumber
            ? `${contact.countryCode || ""} ${contact.mobileNumber}`
            : null
          : settings.mobileNumber && contact.mobileNumber
            ? `${contact.countryCode || ""} ${contact.mobileNumber}`
            : null,
      email: contact.email,
      lawyerId: !isClient && settings.lawyerId ? contact.lawyerId : null,
      location: !isClient && settings.location ? contact.location : null,
    };

    return (
      <div className="flex-1 mr-4">
        {details.name && (
          <div className="flex items-center gap-2">
            <p className="text-white font-medium">{details.name}</p>
            {isClient && (
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30">
                Client
              </span>
            )}
          </div>
        )}
        <div className="space-y-1">
          {details.email && (
            <p className="text-gray-400 text-sm">{details.email}</p>
          )}
          {details.mobileNumber && (
            <p className="text-gray-400 text-sm">{details.mobileNumber}</p>
          )}
          {details.lawyerId && (
            <p className="text-gray-400 text-sm">ID: {details.lawyerId}</p>
          )}
          {details.location && (
            <p className="text-gray-400 text-sm">{details.location}</p>
          )}
        </div>
      </div>
    );
  };

  // Render dropdown options
  const renderDropdownOptions = () => {
    const searchOptions = [
      {
        type: "search",
        value: "Name",
        icon: <User size={16} className="text-gray-400" />,
      },
      {
        type: "search",
        value: "Mobile Number",
        icon: <Phone size={16} className="text-gray-400" />,
      },
      {
        type: "search",
        value: "Email",
        icon: <Mail size={16} className="text-gray-400" />,
      },
    ];
    const clientOption = {
      type: "search",
      value: "Clients",
      icon: <UserCheck size={16} className="text-gray-400" />,
    };
    const teamOptions = userTeams
      .filter((team) => !team.isDefault)
      .map((team) => ({
        type: "team",
        value: team.id,
        displayName: team.teamName,
        icon: <Users size={16} className="text-gray-400" />,
      }));

    return (
      <>
        {searchOptions.map((opt) => (
          <button
            key={`search-${opt.value}`}
            onClick={() => handleOptionSelect(opt)}
            className="w-full px-4 py-2 text-left hover:bg-gray-700 text-gray-300 transition-colors flex items-center gap-3 text-sm"
          >
            {opt.icon}
            {opt.value}
          </button>
        ))}
        <hr className="my-1 border-gray-700" />
        <button
          key="search-Clients"
          onClick={() => handleOptionSelect(clientOption)}
          className="w-full px-4 py-2 text-left hover:bg-gray-700 text-gray-300 transition-colors flex items-center gap-3 text-sm"
        >
          {clientOption.icon}
          Clients
        </button>
        {teamOptions.length > 0 && <hr className="my-1 border-gray-700" />}
        {loadingTeams && (
          <div className="px-4 py-2 text-sm text-gray-500 text-center">
            Loading teams...
          </div>
        )}
        {teamOptions.map((opt) => (
          <button
            key={`team-${opt.value}`}
            onClick={() => handleOptionSelect(opt)}
            className="w-full px-4 py-2 text-left hover:bg-gray-700 text-gray-300 transition-colors flex items-center gap-3 text-sm"
          >
            {opt.icon}
            {opt.displayName}
          </button>
        ))}
      </>
    );
  };

  // Filter access lists based on local search, excluding the owner
  const ownerId = caseData?.owner || globalUser?.uid;
  const filteredIndividualAccess = (caseData.teamMembers || []).filter((member) =>
    member.uid !== ownerId &&
    member.uid !== globalUser?.uid &&
    (member.name?.toLowerCase().includes(localSearchQuery.toLowerCase()) ||
    member.email?.toLowerCase().includes(localSearchQuery.toLowerCase()))
  );

  const filteredTeamAccess = teamAccessList.filter((team) =>
    team.name?.toLowerCase().includes(localSearchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Team Members Usage Banner */}
      {teamLimitInfo && (
        <div className="flex items-center justify-between bg-gray-800 rounded-lg border border-gray-700 px-4 py-3">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-[#D19B15]" />
            <span className="text-gray-300 text-sm font-medium">
              Team Members: {teamLimitInfo.currentCount}/{teamLimitInfo.maxLimit >= 999999 ? '∞' : teamLimitInfo.maxLimit}
            </span>
          </div>
          <span className="text-gray-500 text-xs">{teamLimitInfo.planName}</span>
        </div>
      )}

      {/* Search and Add Access Section */}
      <div className="space-y-4">
        <div className="relative">
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={
                selectedOption.type === "team"
                  ? `Selected Team: ${userTeams.find((t) => t.id === selectedOption.value)
                    ?.teamName || "..."
                  }`
                  : `Search by ${selectedOption.value}`
              }
              className="flex-1 px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#D19B15]"
              disabled={selectedOption.type === "team" || isUpdating}
            />
            <button
              type="button"
              onClick={() => setShowDropdown(!showDropdown)}
              className="p-2 rounded-lg bg-gray-700 text-gray-400 hover:text-white border border-gray-600"
              aria-label="Select search type or team"
              disabled={isUpdating}
            >
              <MoreVertical size={20} />
            </button>
            {loadingSearch && (
              <Loader2
                className="absolute right-12 top-1/2 -translate-y-1/2 animate-spin text-gray-500"
                size={20}
              />
            )}
          </div>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div
              ref={dropdownRef}
              className="absolute right-0 top-full mt-2 w-48 bg-gray-800 rounded-lg shadow-lg z-10 overflow-hidden border border-gray-700"
            >
              {renderDropdownOptions()}
            </div>
          )}
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        {/* Search Results */}
        {showSearchResults && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            {/* Header with Grant All Access toggle for team members */}
            {showTeamMembers && searchResults.length > 0 && (
              <div className="p-3 bg-gray-750 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-gray-300 font-medium">
                  Team Members ({searchResults.length})
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">Grant All Access</span>
                  <ToggleButton
                    isChecked={isGlobalToggleChecked}
                    onToggle={handleGlobalToggle}
                    disabled={isUpdating || loadingMembers}
                  />
                </div>
              </div>
            )}

            {/* Loading state */}
            {loadingMembers && (
              <div className="p-6 text-center text-gray-400">
                <Loader2 className="animate-spin inline-block mr-2" size={20} />
                Loading members...
              </div>
            )}

            {/* Empty team message */}
            {!loadingMembers && showTeamMembers && searchResults.length === 0 && (
              <div className="p-6 text-center text-gray-400">
                This team has no members.
              </div>
            )}

            {/* Results list */}
            {!loadingMembers && searchResults.length > 0 && (
              <div className="max-h-64 overflow-y-auto">
                {searchResults.map((result) => (
                  <div
                    key={result.uid}
                    className="flex items-start justify-between p-3 border-b border-gray-700 last:border-b-0 hover:bg-gray-750"
                  >
                    {renderContactDetails(result)}
                    <ToggleButton
                      isChecked={memberToggleStates[result.uid] || false}
                      onToggle={() => handleSearchResultToggle(result.uid)}
                      disabled={isUpdating}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Current Access Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-[#D19B15] font-medium">Current Access</h3>
          <input
            type="text"
            placeholder="Search current access..."
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#D19B15] w-64"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Individual Access */}
          <div className="space-y-3">
            <h4 className="text-gray-400 text-sm font-medium">Individual Access</h4>
            <div className="bg-gray-800 rounded-lg border border-gray-700 min-h-[200px] max-h-[240px] overflow-y-auto">
              {filteredIndividualAccess.length > 0 ? (
                <div className="divide-y divide-gray-700">
                  {filteredIndividualAccess.map((member) => (
                    <div
                      key={member.uid}
                      className="flex items-center justify-between p-4 hover:bg-gray-750"
                    >
                      <div className="flex items-center">
                        <div className={`w-10 h-10 ${member._isClient ? "bg-purple-600" : "bg-green-600"} rounded-lg mr-3 flex items-center justify-center`}>
                          {member._isClient ? <UserCheck size={20} className="text-white" /> : <User size={20} className="text-white" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{member.name}</p>
                            {member._isClient && (
                              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30">
                                Client
                              </span>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm">{member.email}</p>
                        </div>
                      </div>
                      <ToggleButton
                        isChecked={true}
                        onToggle={() => handleRemoveIndividualAccess(member.uid)}
                        disabled={isUpdating}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-[200px]">
                  <p className="text-gray-500">No individual access granted</p>
                </div>
              )}
            </div>
          </div>

          {/* Team Access */}
          <div className="space-y-3">
            <h4 className="text-gray-400 text-sm font-medium">Team Access</h4>
            <div className="bg-gray-800 rounded-lg border border-gray-700 min-h-[200px] max-h-[240px] overflow-y-auto">
              {filteredTeamAccess.length > 0 ? (
                <div className="divide-y divide-gray-700">
                  {filteredTeamAccess.map((team) => (
                    <div
                      key={team.id}
                      className="flex items-center justify-between p-4 hover:bg-gray-750"
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg mr-3 flex items-center justify-center">
                          <Users size={20} className="text-white" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{team.name}</p>
                          <p className="text-gray-400 text-sm">
                            {team.memberCount} members
                          </p>
                        </div>
                      </div>
                      <ToggleButton
                        isChecked={true}
                        onToggle={() => handleRemoveTeamAccess(team.members)}
                        disabled={isUpdating}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-[200px]">
                  <p className="text-gray-500">No team access granted</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sharing Limit Dialog */}
      <SharingLimitDialog
        isOpen={showLimitDialog}
        onClose={() => setShowLimitDialog(false)}
        currentCount={limitDialogData.currentCount}
        maxLimit={limitDialogData.maxLimit}
        planName={limitDialogData.planName}
        upgradeMessage={limitDialogData.upgradeMessage}
      />
    </div>
  );
}

export default AllowAccess;